import { formatInTimeZone } from 'date-fns-tz'

/** Quiet-hours helpers; curated IANA list lives in utils/timezones. */

export { formatTimezoneLabel, guessBrowserTimezone } from 'utils/timezones'

/** Normalize API "HH:MM:SS" / "HH:MM" to HTML time input value "HH:MM". */
export function timeToInputValue(value: string | null | undefined): string {
  if (!value) return ''
  const match = /^(\d{2}):(\d{2})/.exec(value)
  return match ? `${match[1]}:${match[2]}` : ''
}

/** HTML time "HH:MM" → API "HH:MM:00". */
export function inputValueToTimeApi(value: string): string | null {
  const match = /^(\d{2}):(\d{2})$/.exec((value || '').trim())
  if (!match) return null
  return `${match[1]}:${match[2]}:00`
}

/** API "HH:MM[:SS]" → Date for 24h TimePicker (date part ignored). */
export function apiTimeToPickerDate(value: string | null | undefined): Date | null {
  const hhmm = timeToInputValue(value)
  if (!hhmm) return null
  const [hh, mm] = hhmm.split(':').map(Number)
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null
  return new Date(1970, 0, 1, hh, mm, 0, 0)
}

/** TimePicker Date → API "HH:MM:00". */
export function pickerDateToApiTime(value: Date | null): string | null {
  if (!value || Number.isNaN(value.getTime())) return null
  const h = String(value.getHours()).padStart(2, '0')
  const m = String(value.getMinutes()).padStart(2, '0')
  return `${h}:${m}:00`
}

export const DEFAULT_QUIET_HOURS_START = '22:00:00'
export const DEFAULT_QUIET_HOURS_END = '07:00:00'

function parseHhMmSsToSeconds(value: string | null | undefined): number | null {
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?/.exec((value || '').trim())
  if (!match) return null
  const h = Number(match[1])
  const m = Number(match[2])
  const s = Number(match[3] || 0)
  if (![h, m, s].every(Number.isFinite)) return null
  return h * 3600 + m * 60 + s
}

/** Same overnight / exclusive-end rules as API `is_in_quiet_hours`. */
export function isLocalTimeInQuietHours(params: {
  enabled: boolean
  start: string | null | undefined
  end: string | null | undefined
  timezone?: string | null
  now?: Date
}): boolean {
  if (!params.enabled) return false
  const start = parseHhMmSsToSeconds(params.start)
  const end = parseHhMmSsToSeconds(params.end)
  if (start == null || end == null) return false

  const now = params.now ?? new Date()
  const tz = (params.timezone || 'UTC').trim() || 'UTC'
  let local = ''
  try {
    local = formatInTimeZone(now, tz, 'HH:mm:ss')
  } catch {
    local = formatInTimeZone(now, 'UTC', 'HH:mm:ss')
  }
  const current = parseHhMmSsToSeconds(local)
  if (current == null) return false
  if (start <= end) {
    return start <= current && current < end
  }
  return current >= start || current < end
}
